import {ReactNode} from "react";
import classNames from "classnames";
import {Modal, ModalSize} from "./Modal";
import "./Overlay.scss";

/**
 * Overlay — responsive dialog. Centered modal on desktop; docks to a bottom
 * sheet on mobile / tablet (slide-up). Replaces the retired legacy overlay
 * + the legacy bottom panel; built on the themed `widgets/Modal` so there is one
 * overlay system, not three. Sheet behavior is pure CSS on the Modal, so no
 * separate animated bottom-panel component is needed.
 *
 * `modalOnTablet` / `modalOnMobile` keep it a centered dialog at that
 * breakpoint instead of a sheet (mirrors the AutoPanel API).
 */
interface OverlayProps {
    isOpen: boolean;
    children: ReactNode;
    toggle?: () => void;
    size?: ModalSize;
    contentClassName?: string;
    containerClassName?: string;
    disableOverlayDismiss?: boolean;
    /** Stay a centered dialog on tablet instead of a bottom sheet. */
    modalOnTablet?: boolean;
    /** Stay a centered dialog on mobile instead of a bottom sheet. */
    modalOnMobile?: boolean;
    /** Sheet takes up to 90% viewport height. */
    maxHeight?: boolean;
    /** Sheet takes the full viewport height. */
    fullScreenHeight?: boolean;
    /** Opt out of the maxHeight cap. */
    disableFullHeight?: boolean;
}

export function Overlay({
    isOpen,
    children,
    toggle,
    size,
    contentClassName,
    containerClassName,
    disableOverlayDismiss,
    modalOnTablet,
    modalOnMobile,
    maxHeight,
    fullScreenHeight,
    disableFullHeight,
}: OverlayProps): JSX.Element {
    const sheetClass = classNames("dp-overlay", {
        "dp-overlay--dialog-tablet": modalOnTablet,
        "dp-overlay--dialog-mobile": modalOnMobile,
        "dp-overlay--max-height": maxHeight && !disableFullHeight,
        "dp-overlay--full-height": fullScreenHeight,
    });

    return (
        <Modal
            isOpen={isOpen}
            toggle={toggle}
            size={size}
            disableOverlayDismiss={disableOverlayDismiss}
            containerClassName={classNames(sheetClass, containerClassName)}
            contentClassName={contentClassName}
        >
            {children}
        </Modal>
    );
}

export default Overlay;
